import { Navigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ROUTES } from '@/lib/routes';

/**
 * Maps each route path pattern to the menuId required to access it.
 * Routes not listed here are accessible to all authenticated users (e.g. dashboard).
 */
const ROUTE_MENU_MAP: Record<string, string> = {
  [ROUTES.ENTITY_REGISTRY]: 'entity-registry',
  [ROUTES.ENTITY_REGISTRY_NEW]: 'entity-registry',
  [ROUTES.ENTITY_REGISTRY_EDIT]: 'entity-registry',
  [ROUTES.ENTITY_REGISTRY_GRAPH]: 'entity-registry',
  [ROUTES.MAPPINGS]: 'mappings',
  [ROUTES.MAPPING_NEW]: 'mappings',
  [ROUTES.MAPPING_EDIT]: 'mappings',
  [ROUTES.SCHEMAS]: 'schemas',
  [ROUTES.SCHEMA_NEW]: 'schemas',
  [ROUTES.SCHEMA_EDIT]: 'schemas',
  [ROUTES.LOOKUP_TABLES]: 'lookup-tables',
  [ROUTES.LOOKUP_TABLE_NEW]: 'lookup-tables',
  [ROUTES.LOOKUP_TABLE_EDIT]: 'lookup-tables',
  [ROUTES.UOM_CONVERSIONS]: 'uom-conversions',
  [ROUTES.UOM_CONVERSION_NEW]: 'uom-conversions',
  [ROUTES.UOM_CONVERSION_EDIT]: 'uom-conversions',
  [ROUTES.CONNECTIONS]: 'connections',
  [ROUTES.SYSTEMS]: 'systems',
  [ROUTES.WORKFLOWS]: 'workflows',
  [ROUTES.DEAD_LETTER_QUEUES]: 'dlq',
  [ROUTES.USERS]: 'users',
  [ROUTES.COMPANIES]: 'companies',
  [ROUTES.SETTINGS]: 'settings',
  [ROUTES.DEMO_INBOX]: 'demo-inbox',
  [ROUTES.QUOTA]: 'quota',
  [ROUTES.AUDIT_TRAIL]: 'audit-trail',
  [ROUTES.INTEGRATION_AUDIT]: 'integration-audit',
  [ROUTES.CONFIG_SYNC]: 'config-sync',
  [ROUTES.POLLING_STATUS]: 'polling-status',
  [ROUTES.RECONCILIATION]: 'reconciliation',
  [ROUTES.ALERTS]: 'alerts',
  [ROUTES.INITIAL_LOAD]: 'initial-load',
  [ROUTES.INITIAL_LOAD_DETAIL]: 'initial-load',
  [ROUTES.DATA_HUB]: 'data-hub',
  [ROUTES.DATA_HUB_DASHBOARDS]: 'data-hub-dashboards',
  [ROUTES.DATA_HUB_EXPLORE]: 'data-hub-explore',
  [ROUTES.DATA_HUB_REPORTS]: 'data-hub-reports',
  [ROUTES.DATA_HUB_AGENTS]: 'data-hub-agents',
  [ROUTES.DATA_HUB_POWERBI]: 'data-hub-powerbi',
  [ROUTES.DATA_HUB_TABLES]: 'data-hub-tables',
  [ROUTES.DOCUMENTS]: 'documents',
  [ROUTES.DOCUMENT_REVIEW]: 'documents',
  [ROUTES.DOCUMENTS_INSTRUCTIONS]: 'documents-instructions',
};

interface MenuGuardProps {
  routePattern: string;
  children: React.ReactNode;
}

/**
 * Route-level guard that checks whether the current user has menu access
 * for the given route. If not, redirects to the dashboard.
 */
export function MenuGuard({ routePattern, children }: MenuGuardProps) {
  const { menus } = useAuth();

  // Business users (Data Hub / Documentos only) have no technical dashboard:
  // their home is the first module they can open.
  if (routePattern === ROUTES.DASHBOARD && menus.length > 0 && !menus.some((m) => m.menuId === 'dashboard')) {
    const first = [...menus].sort((a, b) => a.order - b.order)[0];
    if (first && first.path !== ROUTES.DASHBOARD) return <Navigate to={first.path} replace />;
  }

  const requiredMenuId = ROUTE_MENU_MAP[routePattern];

  // No menu restriction for this route (e.g. dashboard, setup)
  if (!requiredMenuId) {
    return <>{children}</>;
  }

  const hasAccess = menus.some((m) => m.menuId === requiredMenuId);

  if (!hasAccess) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}
